import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { Modal } from "src/design-system/Modal";
import { usePromoteFlowVersionToManifest } from "src/flowContainer/hooks/useManifestBuilder";
import { PromoteFlowVersionForm } from "src/flowContainer/ManifestPromotion/PromoteFlowVersionForm";
import { promoteFlowVersionSchema } from "src/flowContainer/ManifestPromotion/schema";
import { PromoteFlowVersionFormData } from "src/flowContainer/ManifestPromotion/types";
import {
  CustomConnectionNodeData,
  extractCustomConnectionNodeData,
} from "src/flowContainer/ManifestPromotion/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useAuthStore } from "src/store/AuthStore";

type PromoteFlowVersionModalProps = {
  open: boolean;
  onClose: () => void;
  flowVersionId: string;
  afterLeave?: () => void;
};

export const PromoteFlowVersionModal: React.FC<
  PromoteFlowVersionModalProps
> = ({ open, onClose, flowVersionId, afterLeave }) => {
  const { workspace, version } = useAuthoringContext();
  const userEmail = useAuthStore((s) => s.signed_in_email);
  const { mutateAsync: promoteFlowVersion, isPending } =
    usePromoteFlowVersionToManifest();

  const customNodeData: CustomConnectionNodeData | null =
    extractCustomConnectionNodeData(version.graph);

  const form = useForm<PromoteFlowVersionFormData>({
    resolver: yupResolver(promoteFlowVersionSchema),
    defaultValues: {
      providerType: "new",
      onePasswordLinks: [],
      documentations: [],
      errorHandling: "",
      comments: "",
    },
  });

  const handleSubmit = async (data: PromoteFlowVersionFormData) => {
    await promoteFlowVersion({
      flowVersionId,
      providerType: data.providerType,
      existingProviderName: data.existingProviderName,
      newProviderDescription: data.newProviderDescription,
      onePasswordLinks: data.onePasswordLinks,
      documentations: data.documentations,
      errorHandling: data.errorHandling,
      comments: data.comments,
      userEmail: userEmail ?? "",
    });
    onClose();
  };

  return (
    <Modal
      afterLeave={() => {
        afterLeave?.();
        form.reset();
      }}
      open={open}
      size="sm"
      onClose={onClose}
    >
      <Modal.Header description="Provide context for the integration team for production review.">
        Promote Decision Flow to integration{" "}
      </Modal.Header>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Modal.Content>
          <PromoteFlowVersionForm
            baseUrl={workspace.base_url}
            customNodeData={customNodeData}
            form={form}
          />
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="promote-flow-version-submit"
              htmlType="submit"
              loading={isPending}
              variant="primary"
            >
              Promote
            </Button>
          }
          secondaryButton={
            <Button disabled={isPending} variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
